import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Moon, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DatePicker } from "../components/DatePicker";
import { NumberPicker, padNum } from "../components/NumberPicker";
import { AddressFields } from "../components/AddressFields";
import { IMPACT_LINE } from "./Impact";

const EMPTY_ADDRESS = { full_name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India" };

export default function Create() {
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [edition, setEdition] = useState((params.get("edition") || "CELESTIAL").toUpperCase());
  const [number, setNumber] = useState(null);
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [form, setForm] = useState({
    registry_type: params.get("type") === "MOON" ? "MOON" : "STAR",
    recipient_name: "",
    dedication_name: "",
    dedication_message: "",
    occasion: "",
    dedication_date: "",
    purchase_type: "SELF",
    promotion_code: "",
  });

  useEffect(() => { api.get("/pricing").then(({ data }) => setPricing(data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const price = pricing?.editions?.find((e) => e.edition === edition)?.price ?? pricing?.next_price;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to create a registry record");
      navigate("/login?next=/create");
      return;
    }
    if (!number) { toast.error("Please choose a registry number"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/registries", {
        ...form,
        edition,
        registry_number: number,
        delivery_address: address,
        promotion_code: form.promotion_code.trim() || null,
        dedication_date: form.dedication_date || null,
      });
      navigate(`/checkout/${data.registry_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create registry record");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-24" data-testid="create-page">
      <div className="hy-label">New Registration</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">Create a commemorative record</h1>
      <p className="text-sm text-slate-400 mt-6 max-w-2xl leading-relaxed">
        This creates a symbolic dedication record in the private HYLUNER registry. It does not transfer
        ownership of any celestial body.
      </p>

      <div className="grid lg:grid-cols-3 gap-10 mt-14">
        <form onSubmit={submit} className="lg:col-span-2 hy-card p-8 space-y-9" data-testid="create-form">
          <NumberPicker edition={edition} editions={pricing?.editions} value={number}
            onEdition={setEdition} onChange={setNumber} />

          <div className="hy-rule" />

          <div>
            <div className="hy-label mb-4">Registry Type</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[["STAR", Star, "Star Dedication", "ICRS / J2000.0 coordinates"],
                ["MOON", Moon, "Moon Parcel", "Selenographic coordinates"]].map(([val, Icon, label, sub]) => (
                <button key={val} type="button" onClick={() => setForm({ ...form, registry_type: val })}
                  data-testid={`type-select-${val.toLowerCase()}`}
                  className={`text-left p-5 border transition-all duration-300 ${form.registry_type === val ? "border-amber-400 bg-amber-500/10" : "border-amber-500/20 hover:border-amber-500/50"}`}>
                  <Icon className="w-5 h-5" style={{ color: "var(--hy-gold)" }} strokeWidth={1.4} />
                  <div className="text-white mt-3 text-sm">{label}</div>
                  <div className="text-xs text-slate-500 mt-1">{sub}</div>
                </button>
              ))}
            </div>
          </div>

          <Field label="Dedication Name" hint="The name shown on the certificate and public registry">
            <input required value={form.dedication_name} onChange={set("dedication_name")} data-testid="input-dedication-name"
              className="hy-input" placeholder="e.g. Aria's Light" />
          </Field>

          <Field label="Recipient Name" hint="Who this dedication is for">
            <input required value={form.recipient_name} onChange={set("recipient_name")} data-testid="input-recipient-name"
              className="hy-input" placeholder="e.g. Aria Mehta" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Occasion">
              <input value={form.occasion} onChange={set("occasion")} data-testid="input-occasion" className="hy-input" placeholder="Birthday, Memorial, Anniversary" />
            </Field>
            <Field label="Dedication Date">
              <DatePicker value={form.dedication_date}
                onChange={(v) => setForm({ ...form, dedication_date: v })}
                placeholder="Choose a date" testId="input-dedication-date" />
            </Field>
          </div>

          <Field label="Dedication Message" hint="Printed on the certificate (optional)">
            <textarea rows={3} value={form.dedication_message} onChange={set("dedication_message")} data-testid="input-message" className="hy-input" />
          </Field>

          <div className="hy-rule" />
          <AddressFields value={address} onChange={setAddress} />
          <div className="hy-rule" />

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Purchase Type">
              <select value={form.purchase_type} onChange={set("purchase_type")} data-testid="input-purchase-type" className="hy-input">
                <option value="SELF">For myself</option>
                <option value="GIFT">As a gift</option>
              </select>
            </Field>
            <Field label="Promotional Code" hint="Optional">
              <input value={form.promotion_code} onChange={set("promotion_code")} data-testid="input-promo" className="hy-input" placeholder="GENESIS10" />
            </Field>
          </div>

          <button type="submit" disabled={submitting || !number} data-testid="create-submit-btn"
            className="hy-btn w-full py-4 text-xs uppercase flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {number ? `Reserve ${padNum(number)}` : "Choose a registry number"}
          </button>
          {!loading && !user && (
            <p className="text-xs text-amber-300/80 text-center">
              You'll need to <Link to="/login?next=/create" className="underline">sign in</Link> to complete this step.
            </p>
          )}
        </form>

        <aside className="hy-glass p-8 h-fit lg:sticky lg:top-28" data-testid="create-summary">
          <div className="hy-label">Your Position</div>
          {pricing ? (
            <>
              <div className="font-display text-3xl text-amber-200 mt-4" data-testid="summary-number">
                {number ? padNum(number) : "—"}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2">{edition} Edition</div>
              <div className="text-[0.7rem] text-slate-500 mt-2">Your chosen registry number</div>
              <div className="hy-rule my-7" />
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Edition price</span>
                <span className="text-white" data-testid="summary-price">{money(price)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-5 leading-relaxed">
                Includes the physical photo-framed certificate, your digital certificate and QR verification.
              </p>
              <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                Your number is held for 30 minutes while you complete payment, and becomes permanently yours
                once payment is confirmed. It is never reissued.
              </p>
              <div className="hy-rule my-7" />
              <p className="text-xs text-amber-200/80 leading-relaxed" data-testid="create-impact-note">{IMPACT_LINE}</p>
            </>
          ) : <Loader2 className="w-4 h-4 animate-spin text-amber-300 mt-4" />}
        </aside>
      </div>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <div className="hy-label mb-3">{label}</div>
    {children}
    {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
  </div>
);
